import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { ColumnDef } from '@tanstack/react-table';
import { Eye, UserRound } from 'lucide-react';
import { PageHeader } from '@/shared/ui/layout/PageHeader';
import { IconButton } from '@/shared/ui/buttons/IconButton';
import { ErrorState } from '@/shared/ui/feedback/ErrorState';
import { DataTable } from '@/shared/ui/data-table/DataTable';
import { Pagination } from '@/shared/ui/data-table/Pagination';
import { FilterBar } from '@/shared/ui/data-table/FilterBar';
import { extractErrorMessage } from '@/shared/api/errors';
import { useClientsList } from '@/entities/client';
import { useDebouncedValue } from '@/shared/hooks/useDebouncedValue';
import { formatDate } from '@/shared/utils/date';
import { ClientBlockChip } from '@/shared/ui/chips/ClientBlockChip';
import type { ApiClientGetItem } from '@/shared/types/api/clients';

const PAGE_SIZE = 20;

export const WidgetsClientsMain = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 350);

  const params = useMemo(
    () => ({
      page,
      page_size: PAGE_SIZE,
      search: debouncedSearch.trim() || undefined,
    }),
    [page, debouncedSearch],
  );

  const query = useClientsList(params);

  const columns = useMemo<ColumnDef<ApiClientGetItem>[]>(
    () => [
      {
        accessorKey: 'email',
        header: t('clients.list.columns.email'),
        cell: (info) => (
          <span className="font-medium text-ink">{info.row.original.email}</span>
        ),
      },
      {
        accessorKey: 'full_name',
        header: t('clients.list.columns.fullName'),
        cell: (info) =>
          info.row.original.full_name?.trim() || (
            <span className="text-ink-faint">{t('clients.empty')}</span>
          ),
      },
      {
        accessorKey: 'phone_number',
        header: t('clients.list.columns.phone'),
        cell: (info) =>
          info.row.original.phone_number?.trim() || (
            <span className="text-ink-faint">{t('clients.empty')}</span>
          ),
      },
      {
        id: 'status',
        header: t('clients.list.columns.status'),
        enableSorting: false,
        cell: (info) => (
          <ClientBlockChip blocked={info.row.original.is_parcel_creation_blocked} />
        ),
      },
      {
        accessorKey: 'created_at',
        header: t('clients.list.columns.createdAt'),
        cell: (info) => formatDate(info.row.original.created_at),
      },
      {
        id: 'actions',
        header: t('clients.list.columns.actions'),
        enableSorting: false,
        cell: (info) => (
          <IconButton
            label={t('clients.list.actions.view')}
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/clients/${info.row.original.id}`);
            }}
          >
            <Eye className="h-4 w-4" />
          </IconButton>
        ),
      },
    ],
    [navigate, t],
  );

  return (
    <>
      <PageHeader title={t('clients.list.title')} subtitle={t('clients.list.subtitle')} />
      <div className="space-y-4 px-4 py-4 lg:px-6 lg:py-6">
        <FilterBar
          searchValue={search}
          onSearchChange={(v) => {
            setPage(1);
            setSearch(v);
          }}
          searchPlaceholder={t('clients.list.searchPlaceholder')}
        />

        {query.error ? (
          <ErrorState
            message={extractErrorMessage(query.error, t('clients.list.loadError'))}
            onRetry={() => void query.refetch()}
          />
        ) : (
          <>
            <DataTable
              data={query.data?.results ?? []}
              columns={columns}
              isLoading={query.isLoading}
              emptyTitle={t('clients.list.emptyTitle')}
              emptyDescription={t('clients.list.emptyDescription')}
              emptyIcon={<UserRound className="h-8 w-8" aria-hidden />}
              onRowClick={(row) => navigate(`/clients/${row.id}`)}
              getRowId={(row) => row.id}
              numbered
              pageIndex={page - 1}
              pageSize={PAGE_SIZE}
            />
            <Pagination
              page={page}
              pageSize={PAGE_SIZE}
              total={query.data?.count ?? 0}
              onPageChange={setPage}
            />
          </>
        )}
      </div>
    </>
  );
};
